/**
 * product-page.js
 * XStamper — Product page specific behaviour
 *
 * Handles everything product-interactions.js does NOT:
 *   1. Product gallery  — thumbnail clicks → main image swap (fade)
 *   2. Variant selection — ink swatches + option chips → find matching
 *      variant → update price, availability, ATC data-variant-id
 *   3. Quantity selector — increment / decrement
 *   4. Sticky ATC bar   — IntersectionObserver on .product-info__header
 *   5. Tab switching    — [data-tab] buttons ↔ #panel-{id} panels
 *   6. Buy It Now       — direct /cart/{id}:{qty}?checkout redirect
 *
 * Does NOT handle: data-add-to-cart, data-wishlist (product-interactions.js)
 *
 * Requires on window before this file runs (set by product.liquid):
 *   window.ProductData       — { [productId]: { variants: [...] } }
 *   window.CurrentProductId  — numeric product id
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     SHARED STATE
  ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Keyed by option position index (0-based integer).
   * e.g. { 0: 'Blue', 1: '3-Pack', 2: 'Fine' }
   */
  var selectedOptions = {};

  /* ═══════════════════════════════════════════════════════════════════════
     UTILITIES
  ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Format Shopify price integer (cents) → "$12.99"
   * Mirrors the helper already in product-interactions.js (scoped separately).
   */
  function formatMoney(cents) {
    if (window.themeFormatMoney) return window.themeFormatMoney(cents);
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ═══════════════════════════════════════════════════════════════════════
     1. GALLERY
  ═══════════════════════════════════════════════════════════════════════ */

  function initGallery() {
    var track = document.getElementById('gallery-thumbs');
    if (!track) return;

    // Event delegation on the thumb track — catches all thumb clicks.
    // mainImg is looked up at click-time (not init-time) so we never
    // silently bail due to a null reference during initialisation.
    track.addEventListener('click', function (e) {
      var thumb = e.target.closest('.product-gallery__thumb');
      if (!thumb) return;

      var mainImg = document.getElementById('main-product-image');
      var viewer  = document.getElementById('gallery-media-viewer');

      function markActive() {
        track.querySelectorAll('.product-gallery__thumb').forEach(function (t) {
          t.classList.remove('is-active');
        });
        thumb.classList.add('is-active');
      }

      // ── Video / 3D media thumbnail: reveal the media viewer over the image ──
      if (thumb.classList.contains('product-gallery__thumb--media')) {
        var mIndex = thumb.getAttribute('data-media-index');
        if (viewer) {
          viewer.hidden = false;
          viewer.querySelectorAll('.product-gallery__media-item').forEach(function (item) {
            var match = item.getAttribute('data-media-index') === mIndex;
            item.hidden = !match;
            if (!match) {
              var pv = item.querySelector('video');
              if (pv) { try { pv.pause(); } catch (err) {} }
            }
          });
        }
        if (mainImg) mainImg.style.display = 'none';
        markActive();
        return;
      }

      // ── Image thumbnail: restore the image, hide the media viewer ──
      var src     = thumb.getAttribute('data-src');
      var fullSrc = thumb.getAttribute('data-full');
      if (!src || !mainImg) return;

      if (viewer) {
        viewer.hidden = true;
        var vv = viewer.querySelector('video');
        if (vv) { try { vv.pause(); } catch (err) {} }
      }
      mainImg.style.display = '';

      // CRITICAL: remove srcset before setting src.
      // Browsers always prefer srcset over src — if we only update src
      // but leave srcset pointing at the old image, the browser ignores
      // our src change and keeps showing the original image.
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');

      // Fade out → swap → fade in
      mainImg.style.transition = 'opacity 0.2s ease';
      mainImg.style.opacity    = '0';
      setTimeout(function () {
        mainImg.src = src;
        if (fullSrc) mainImg.setAttribute('data-full', fullSrc);
        mainImg.style.opacity = '1';
      }, 200);

      markActive();
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     2. VARIANT SELECTION — core logic
  ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Walk the variants array and return the first variant whose option1/2/3
   * values match every entry in the selectedOptions map.
   *
   * @param {Array}  variants        — product.variants array from ProductData
   * @param {Object} selectedOptions — { 0: 'val', 1: 'val', ... }
   * @returns {Object|undefined}
   */
  function findMatchingVariant(variants, selectedOptions) {
    return variants.find(function (v) {
      return Object.keys(selectedOptions).every(function (idx) {
        var key = 'option' + (parseInt(idx, 10) + 1);
        return v[key] === selectedOptions[idx];
      });
    });
  }

  /**
   * After any option change: look up the matching variant and push all
   * DOM updates (price, availability, variant id, gallery image).
   */
  function updateVariant() {
    var productData = window.ProductData && window.ProductData[window.CurrentProductId];
    if (!productData || !productData.variants) return;

    var variant = findMatchingVariant(productData.variants, selectedOptions);
    if (!variant) {
      /* No matching variant for this option combination — block ATC so a
         stale/previous variant can never be added, and tell the shopper. */
      [document.getElementById('product-atc-btn'), document.getElementById('sticky-atc-btn')].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-sold-out');
      });
      if (window.themeAnnounce) window.themeAnnounce('This combination is unavailable');
      return;
    }

    /* — Hidden form input (used by native Shopify form submit fallback) — */
    var variantInput = document.getElementById('variant-id');
    if (variantInput) variantInput.value = variant.id;

    /* — Sync each option's visible selection label to the chosen variant.
         Authoritative: runs on every variant change regardless of how the
         swatch/chip was rendered, so the shown name (e.g. "Dark Blue") always
         matches. Strips any " | subtext" and " -productcode" suffix.        */
    var optSections = document.querySelectorAll('.option-section[data-option-index]');
    for (var si = 0; si < optSections.length; si++) {
      var sec = optSections[si];
      var oi  = parseInt(sec.getAttribute('data-option-index'), 10);
      var raw = variant['option' + (oi + 1)];
      if (raw == null) continue;
      var clean = String(raw).split(' -')[0].trim();
      var lbl = sec.querySelector('.option-section__selection');
      if (lbl && clean) lbl.textContent = clean;
    }

    /* — ATC button data-variant-id (read by product-interactions.js AJAX) — */
    var atcBtn    = document.getElementById('product-atc-btn');
    var stickyBtn = document.getElementById('sticky-atc-btn');

    [atcBtn, stickyBtn].forEach(function (btn) {
      if (btn) btn.setAttribute('data-variant-id', variant.id);
    });

    /* — Price elements — */
    var priceEl      = document.getElementById('product-price');
    var atcPriceEl   = document.getElementById('atc-price');
    var stickyPriceEl = document.getElementById('sticky-price');
    var compareEl    = document.getElementById('product-compare-price');

    var formattedPrice = formatMoney(variant.price);

    if (priceEl)       priceEl.textContent       = formattedPrice;
    if (atcPriceEl)    atcPriceEl.textContent    = '— ' + formattedPrice; // em dash
    if (stickyPriceEl) stickyPriceEl.textContent = formattedPrice;

    // A chosen pack size (stamp-options.js) is added on top of the variant
    // price, so hand it the new base and let it re-apply the pack amount.
    if (priceEl) priceEl.setAttribute('data-base-price', variant.price);
    if (window.stampSyncOptionPrice) window.stampSyncOptionPrice();

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent  = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    /* — SKU — */
    var skuWrapEl  = document.getElementById('product-sku');
    var skuValueEl = document.getElementById('product-sku-value');
    if (skuWrapEl && skuValueEl) {
      if (variant.sku) {
        skuValueEl.textContent = variant.sku;
        skuWrapEl.style.display = '';
      } else {
        skuWrapEl.style.display = 'none';
      }
    }

    /* — Availability — */
    var available = variant.available;

    [atcBtn, stickyBtn].forEach(function (btn) {
      if (!btn) return;
      btn.disabled = !available;
      btn.classList.toggle('is-sold-out', !available);
      // Keep aria up to date
      btn.setAttribute('aria-disabled', (!available).toString());
    });

    /* — Hide Buy It Now / dynamic checkout when the selected variant is sold out — */
    var buyNowBtn = document.getElementById('buy-now-btn');
    if (buyNowBtn) buyNowBtn.style.display = available ? '' : 'none';
    var payWrap = document.getElementById('payment-button-wrapper');
    if (payWrap && !available) payWrap.style.display = 'none';

    /* — Sold-out badge on main price area — */
    var soldOutEl = document.getElementById('product-sold-out');
    if (soldOutEl) soldOutEl.style.display = available ? 'none' : '';

    /* — Announce price + availability for screen readers — */
    if (window.themeAnnounce) {
      window.themeAnnounce(formattedPrice + (available ? '' : ', sold out'));
    }

    /* — Gallery: swap to variant image using pre-built variantImages map.
       Strategy: find the thumbnail whose data-src matches the variant's
       image URL, then .click() it — this reuses initGallery()'s proven
       fade + src-swap + active-state logic in one operation.             */
    var variantImages = productData.variantImages || {};
    var varImgUrl     = variantImages[String(variant.id)];

    if (varImgUrl) {
      var thumbs      = document.querySelectorAll('.product-gallery__thumb');
      var targetThumb = null;

      // Normalize: strip protocol, query params, and CDN size suffix (_600x etc)
      function normImg(url) {
        return (url || '')
          .replace(/^https?:/, '')            // // or https:// → //
          .split('?')[0]                      // drop ?v=...&width=...
          .replace(/_\d+x\w*(?=\.(jpg|jpeg|png|gif|webp))/i, ''); // drop _600x
      }

      var varNorm = normImg(varImgUrl);

      for (var k = 0; k < thumbs.length; k++) {
        var tNorm = normImg(thumbs[k].getAttribute('data-src') || '');
        if (tNorm && tNorm === varNorm) {
          targetThumb = thumbs[k];
          break;
        }
      }

      if (targetThumb) {
        if (!targetThumb.classList.contains('is-active')) {
          // Programmatic click → initGallery handler fires:
          //   fades main image, swaps src, marks thumbnail active
          targetThumb.click();
        }
      } else {
        // Fallback: no thumbnail in strip — update main image directly
        var mainImg = document.getElementById('main-product-image');
        if (mainImg) {
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.style.opacity = '0';
          var _url = varImgUrl;
          setTimeout(function () {
            mainImg.src = _url;
            mainImg.style.opacity = '1';
          }, 150);
        }
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     3. INK SWATCHES
  ═══════════════════════════════════════════════════════════════════════ */

  function initInkSwatches() {
    // Event-delegated so it works even if swatches are injected after load
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest('.ink-swatch');
      if (!swatch) return;

      var group   = swatch.closest('[data-swatch-group]');
      if (!group) return;

      // Determine option index from the enclosing [data-option-index] section
      var section     = swatch.closest('[data-option-index]');
      var optionIndex = section ? parseInt(section.getAttribute('data-option-index'), 10) : 0;

      // Update active swatch inside this group only
      group.querySelectorAll('.ink-swatch').forEach(function (s) {
        s.classList.remove('is-active');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('is-active');
      swatch.setAttribute('aria-pressed', 'true');

      // Update the visible selection label using the clean color name (data-color),
      // capitalized — e.g. "red" → "Red", "dark blue" → "Dark Blue"
      if (section) {
        var label = section.querySelector('.option-section__selection');
        if (label) {
          var colorName = swatch.getAttribute('data-color') || swatch.getAttribute('data-value') || '';
          label.textContent = colorName.replace(/\b\w/g, function (l) { return l.toUpperCase(); });
        }
      }

      // Variant matching always uses the full raw data-value so Shopify finds the right variant
      selectedOptions[optionIndex] = swatch.getAttribute('data-value') ||
                                     swatch.getAttribute('data-color') || '';
      updateVariant();
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     4. OPTION CHIPS  (pack size, tip type, etc.)
  ═══════════════════════════════════════════════════════════════════════ */

  function initOptionChips() {
    document.addEventListener('click', function (e) {
      var chip = e.target.closest('.option-chip');
      if (!chip) return;

      // Skip disabled chips
      if (chip.classList.contains('is-disabled')) return;

      var group = chip.closest('[data-option-group]');
      if (!group) return;

      var section     = chip.closest('[data-option-index]');
      var optionIndex = section ? parseInt(section.getAttribute('data-option-index'), 10) : 1;

      // Update active chip inside this group only
      group.querySelectorAll('.option-chip').forEach(function (c) {
        c.classList.remove('is-active');
        c.setAttribute('aria-selected', 'false');
      });
      chip.classList.add('is-active');
      chip.setAttribute('aria-selected', 'true');

      // Update the visible selection label
      if (section) {
        var label = section.querySelector('.option-section__selection');
        if (label) {
          label.textContent = chip.getAttribute('data-value') || chip.textContent.trim();
        }
      }

      selectedOptions[optionIndex] = chip.getAttribute('data-value') || chip.textContent.trim();
      updateVariant();
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     5. QUANTITY SELECTOR
  ═══════════════════════════════════════════════════════════════════════ */

  function initQuantitySelector() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty]');
      if (!btn) return;

      var container = btn.closest('.quantity-selector');
      if (!container) return;

      var input = container.querySelector('.quantity-selector__value');
      if (!input) return;

      var current   = parseInt(input.value, 10) || 1;
      var min       = parseInt(input.min, 10)   || 1;
      var max       = parseInt(input.max, 10)   || 99;
      var direction = btn.getAttribute('data-qty');

      if (direction === 'increase') {
        input.value = Math.min(current + 1, max);
      } else if (direction === 'decrease') {
        input.value = Math.max(current - 1, min);
      }

      // Notify other listeners (e.g. ATC form, buy-now logic)
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     6. STICKY ATC BAR
  ═══════════════════════════════════════════════════════════════════════ */

  function initStickyATC() {
    var trigger   = document.querySelector('.product-info__header');
    var stickyBar = document.getElementById('sticky-atc');

    if (!trigger || !stickyBar) return;

    // Show sticky bar once the product header scrolls out of view
    var observer = new IntersectionObserver(function (entries) {
      var entry   = entries[0];
      var visible = !entry.isIntersecting;
      stickyBar.classList.toggle('is-visible', visible);
      stickyBar.setAttribute('aria-hidden', (!visible).toString());
      // Keep the hidden bar out of the tab order too (aria-hidden alone
      // still leaves its button keyboard-focusable).
      stickyBar.toggleAttribute('inert', !visible);
    }, {
      threshold:  0,
      rootMargin: '-80px 0px 0px 0px' // accounts for sticky site header height
    });

    observer.observe(trigger);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     7. TABS — handled by the section-scoped script inside
     sections/product-tabs.liquid (this file used to double-bind them).
  ═══════════════════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════════════════
     8. BUY IT NOW
  ═══════════════════════════════════════════════════════════════════════ */

  function initBuyNow() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-buy-now]');
      if (!btn) return;

      e.preventDefault();

      var variantInput = document.getElementById('variant-id');
      var qtyInput     = document.getElementById('product-quantity');

      if (!variantInput || !variantInput.value) return;

      var id       = variantInput.value;
      var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      // Navigate directly to Shopify one-page checkout
      window.location.href = '/cart/' + id + ':' + quantity + '?checkout';
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     9. INIT DEFAULT SELECTIONS
     Read whichever chips/swatches are already marked is-active in the HTML
     (Liquid renders the first variant's options as active by default) and
     seed selectedOptions so the first updateVariant() call is accurate.
  ═══════════════════════════════════════════════════════════════════════ */

  function initDefaultSelections() {
    var productData = window.ProductData && window.ProductData[window.CurrentProductId];
    if (!productData) return;

    document.querySelectorAll('[data-option-index]').forEach(function (section) {
      var idx = parseInt(section.getAttribute('data-option-index'), 10);

      // Active ink swatch wins for colour options
      var activeSwatch = section.querySelector('.ink-swatch.is-active');
      if (activeSwatch) {
        selectedOptions[idx] = activeSwatch.getAttribute('data-value') ||
                               activeSwatch.getAttribute('data-color') || '';
        return; // skip chip check for this section
      }

      // Active option chip wins for size/tip options
      var activeChip = section.querySelector('.option-chip.is-active');
      if (activeChip) {
        selectedOptions[idx] = activeChip.getAttribute('data-value') ||
                               activeChip.textContent.trim();
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     BOOT
  ═══════════════════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════════════════
     THUMBNAIL SLIDER
  ═══════════════════════════════════════════════════════════════════════ */

  function initThumbSlider() {
    var track   = document.getElementById('gallery-thumbs');
    var prevBtn = document.querySelector('[data-thumb-prev]');
    var nextBtn = document.querySelector('[data-thumb-next]');

    if (!track || !prevBtn || !nextBtn) return;

    var THUMB_W = 80;   // px — matches .product-gallery__thumb width
    var GAP     = 12;   // px — matches gap in .product-gallery__thumbs
    var STEP    = (THUMB_W + GAP) * 2; // scroll 2 thumbs per click

    function updateArrows() {
      var atStart = track.scrollLeft <= 0;
      var atEnd   = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd || track.scrollWidth <= track.clientWidth;
    }

    prevBtn.addEventListener('click', function () {
      track.scrollLeft -= STEP;
      setTimeout(updateArrows, 320);
    });

    nextBtn.addEventListener('click', function () {
      track.scrollLeft += STEP;
      setTimeout(updateArrows, 320);
    });

    track.addEventListener('scroll', updateArrows, { passive: true });

    // Run once on load — hide next arrow if all thumbs already fit
    updateArrows();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     GALLERY ZOOM / FULLSCREEN LIGHTBOX
  ═══════════════════════════════════════════════════════════════════════ */

  function initGalleryZoom() {
    var zoomBtn = document.querySelector('[data-gallery-zoom]');
    var fsBtn   = document.querySelector('[data-gallery-fullscreen]');
    if (!zoomBtn && !fsBtn) return;

    var lightbox = null;

    function currentImageSrc() {
      var activeThumb = document.querySelector('.product-gallery__thumb.is-active');
      if (activeThumb && activeThumb.getAttribute('data-full')) return activeThumb.getAttribute('data-full');
      var mainImg = document.getElementById('main-product-image');
      if (mainImg) return mainImg.getAttribute('data-full') || mainImg.currentSrc || mainImg.src;
      return '';
    }

    function build() {
      if (lightbox) return lightbox;
      lightbox = document.createElement('div');
      lightbox.className = 'pdp-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Product image');
      lightbox.innerHTML =
        '<button type="button" class="pdp-lightbox__close" aria-label="Close">×</button>' +
        '<img class="pdp-lightbox__img" alt="">';
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox || e.target.closest('.pdp-lightbox__close')) closeLb();
      });
      document.body.appendChild(lightbox);
      return lightbox;
    }

    function openLb(wantFullscreen) {
      var src = currentImageSrc();
      if (!src) return;
      var lb = build();
      var img = lb.querySelector('.pdp-lightbox__img');
      img.src = src;
      var mainImg = document.getElementById('main-product-image');
      if (mainImg) img.alt = mainImg.alt || '';
      lb.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      if (wantFullscreen && lb.requestFullscreen) {
        lb.requestFullscreen().catch(function () {});
      }
    }

    function closeLb() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (document.fullscreenElement) { document.exitFullscreen().catch(function () {}); }
    }

    if (zoomBtn) zoomBtn.addEventListener('click', function () { openLb(false); });
    if (fsBtn)   fsBtn.addEventListener('click', function () { openLb(true); });
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && lightbox && lightbox.classList.contains('is-open')) closeLb();
    });
  }

  function init() {
    initGallery();
    initThumbSlider();
    initGalleryZoom();
    initInkSwatches();
    initOptionChips();
    initQuantitySelector();
    initStickyATC();
    // Tabs are handled by the section-scoped script in sections/product-tabs.liquid
    initBuyNow();
    initDefaultSelections(); // seed selectedOptions from HTML state
    updateVariant();          // sync price + availability on page load
  }

  // Only run on product pages — product.liquid sets window.CurrentProductId
  if (window.CurrentProductId) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

})();
